import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

const CREDIT_TYPE_LABELS = {
  core: 'Core',
  elective: 'Elective',
  agency: 'Agency',
  commissioners_standard: "Commissioner's Standard",
  contract_law: 'Contract Law',
  disclosure: 'Disclosure',
  fair_housing: 'Fair Housing',
  real_estate_legal_issues: 'RE Legal Issues',
  general: 'General',
};

export default function CourseCard({ course, owned, status, compact = false }) {
  const { addToCart, isInCart } = useCart();
  const inCart = isInCart(course.id || course.course_id);
  const isCompleted = status === 'completed';

  const handleAddToCart = async (e) => {
    e.preventDefault();
    try { await addToCart(course.id || course.course_id); }
    catch (err) { alert(err.response?.data?.error || 'Could not add to cart'); }
  };

  const courseId = course.id || course.course_id;
  const thumbHeight = compact ? 110 : 160;
  const bodyPad = compact ? '14px 16px' : '18px 20px';
  const titleSize = compact ? 14 : 16;
  const priceSize = compact ? 17 : 20;
  // For completed courses, link to course details (for re-purchase), otherwise to player for active/not-started
  const linkTo = (owned && !isCompleted) ? `/course/${courseId}/learn` : `/courses/${courseId}`;

  return (
    <Link to={linkTo} style={{ textDecoration: 'none' }}>
      <div style={{ ...styles.card, height: '100%' }}>
        {/* Thumbnail */}
        <div style={{ ...styles.thumb, height: thumbHeight }}>
          {course.thumbnail_url
            ? <img src={course.thumbnail_url} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={styles.thumbPlaceholder}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5">
                  <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
                </svg>
              </div>
          }
          {course.is_featured && !compact && <div style={styles.featuredBadge}>⭐ Featured</div>}
          {owned && !isCompleted && <div style={styles.ownedBadge}>✓ Enrolled</div>}
          {isCompleted && <div style={styles.completedBadge}>✓ Completed</div>}
        </div>

        {/* Body */}
        <div style={{ ...styles.body, padding: bodyPad }}>
          <div style={styles.meta}>
            <span className="badge badge-teal" style={{ fontSize: 10, padding: '2px 8px' }}>
              {CREDIT_TYPE_LABELS[course.credit_type] || 'General'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 600 }}>
              {course.credit_hours} hrs
            </span>
          </div>

          <h3 style={{ ...styles.title, fontSize: titleSize }}>{course.title}</h3>

          {!compact && (
            <p style={styles.desc}>{course.short_description}</p>
          )}

          <div style={styles.footer}>
            <div style={{ ...styles.price, fontSize: priceSize }}>${parseFloat(course.price).toFixed(2)}</div>
            {isCompleted && inCart ? (
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)' }}>✓ In Cart</span>
            ) : isCompleted ? (
              <button className="btn btn-primary btn-sm" onClick={handleAddToCart}>
                Enroll Again?
              </button>
            ) : owned ? (
              <span className="btn btn-secondary btn-sm">
                Continue →
              </span>
            ) : inCart ? (
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)' }}>✓ In Cart</span>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={handleAddToCart}>
                Add to Cart
              </button>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

const styles = {
  card: {
    background: 'white',
    border: '1px solid var(--color-border)',
    borderRadius: 12,
    overflow: 'hidden',
    transition: 'box-shadow 0.2s, transform 0.15s',
    display: 'flex', flexDirection: 'column',
    cursor: 'pointer',
  },
  thumb: {
    background: 'linear-gradient(135deg, #0f766e, #0d9488)',
    position: 'relative', overflow: 'hidden', flexShrink: 0,
  },
  thumbPlaceholder: {
    width: '100%', height: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  featuredBadge: {
    position: 'absolute', top: 8, left: 8,
    background: 'rgba(0,0,0,0.45)', color: 'white',
    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
  },
  ownedBadge: {
    position: 'absolute', top: 8, right: 8,
    background: '#16a34a', color: 'white',
    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
  },
  completedBadge: {
    position: 'absolute', top: 8, right: 8,
    background: '#1d4142', color: 'white',
    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
  },
  body: { display: 'flex', flexDirection: 'column', gap: 8, flex: 1 },
  meta: { display: 'flex', alignItems: 'center', gap: 6 },
  title: { fontWeight: 700, color: 'var(--slate-900)', lineHeight: 1.3 },
  desc: { fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.5, flex: 1 },
  footer: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', marginTop: 'auto',
    paddingTop: 10, borderTop: '1px solid var(--color-border)',
  },
  price: { fontWeight: 800, color: 'var(--color-primary)' },
};